import { useCashflow } from '@/components/cashflow/CashflowContext';
import TableRow, { COLUMNS } from '@/components/cashflow/transactions/TableRow';
import { FlexCol } from '@/components/common/Flex';
import { Atom01 } from '@/components/common/icons';
import { LoadingTrs } from '@/components/common/LoadingTrs';
import { hoverStyleProps } from '@/components/common/styleHelpers';
import { Paragraph } from '@/components/common/Typography';
import usePlaid from '@/components/plaid/usePlaid';
import { sortTransactionEntries } from '@/services/cashflow';
import { Link, Table, TableProps, Th, Thead, Tr } from '@chakra-ui/react';
import {
    CashflowTransactionEntry,
    SortDirection,
} from '@common/react/cashflow/types';
import { useHasNoFEs } from '@common/react/financialEntities';
import { useCurrentUser } from '@common/react/user';
import { DateRange } from '@common/types';
import { dt } from '@common/util/dateTime';
import { parseAsStringEnum, useQueryStates } from 'next-usequerystate';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List } from 'react-window';

export enum SortKey {
    Account = 'account',
    Date = 'date',
    Merchant = 'merchant',
    Category = 'category',
    Amount = 'amount',
}

export const TRANSACTION_ENTRIES_TABLE_Z_INDEX = 2;
const CHART_HEIGHT_PX = 411;
const ROW_HEIGHT_PX = 64;

type TableVariant = 'normal' | 'placeholder';

type CashflowTransactionEntriesTableProps = TableProps & {
    variant?: TableVariant;
};

const CashflowTransactionEntriesTable = ({
    variant = 'normal',
    ...props
}: CashflowTransactionEntriesTableProps) => {
    const { transactionEntries, loading, query, areFiltersApplied } =
        useCashflow();

    return (
        <TransactionEntriesTable
            variant={variant}
            transactionEntries={transactionEntries}
            loading={loading}
            query={query}
            areFiltersApplied={areFiltersApplied}
            {...props}
        />
    );
};

interface TransactionEntriesTableProps {
    transactionEntries: CashflowTransactionEntry[];
    variant?: TableVariant;
    loading?: boolean;
    query?: string | null;
    areFiltersApplied?: boolean;
    // When TransactionEntriesTable is rendered in a drawer, we need a reference
    // to the top-level element in the drawer so that infinite scroll works correctly
    scrollRef?: React.RefObject<HTMLDivElement>;
}

export const TransactionEntriesTable = ({
    variant = 'normal',
    transactionEntries,
    loading = false,
    query = null,
    areFiltersApplied = false,
    scrollRef,
    ...props
}: TransactionEntriesTableProps) => {
    const hasNoFEs = useHasNoFEs();
    const { userInfo } = useCurrentUser();
    const [sortBy, setSortBy] = useQueryStates({
        sortKey: parseAsStringEnum<SortKey>(Object.values(SortKey)).withDefault(
            SortKey.Date
        ),
        direction: parseAsStringEnum<SortDirection>(
            Object.values(SortDirection)
        ).withDefault(SortDirection.Desc),
    });
    const { open } = usePlaid();

    const dateRange = useMemo(
        () => getDateRangeFromTransactionEntries(transactionEntries),
        [transactionEntries]
    );

    // Since viewing the table may update the last viewed transaction ID, store
    // in a ref so it doesn't change until this view is unmounted
    const lastViewedTransactionEntryId = useRef(
        userInfo.lastViewedTransactionEntry?.id
    );

    let sortedTransactionEntries = sortTransactionEntries(
        transactionEntries,
        sortBy.sortKey,
        sortBy.direction
    );

    if (variant === 'placeholder') {
        sortedTransactionEntries = sortedTransactionEntries.slice(0, 20);
    }

    const listRef = useRef<List>(null);
    const onScroll = useCallback(() => {
        if (scrollRef?.current) {
            listRef.current?.scrollTo(scrollRef.current.scrollTop);
        } else {
            listRef.current?.scrollTo(
                document.documentElement.scrollTop - CHART_HEIGHT_PX
            );
        }
    }, [scrollRef]);

    useEffect(() => {
        const currentScrollRef = scrollRef?.current;

        if (currentScrollRef) {
            currentScrollRef.addEventListener('scroll', onScroll);
        } else {
            document.addEventListener('scroll', onScroll);
        }

        return () => {
            if (currentScrollRef) {
                currentScrollRef.removeEventListener('scroll', onScroll);
            } else {
                document.removeEventListener('scroll', onScroll);
            }
        };
    }, [onScroll, scrollRef]);

    return (
        <>
            <Table
                variant="simple"
                position="sticky"
                bg="white"
                // If the user has no FEs, this is just placeholder content
                // Reduce zIndex so ConnectAccountOverlay is above this
                zIndex={hasNoFEs ? 0 : TRANSACTION_ENTRIES_TABLE_Z_INDEX}
                {...props}
            >
                <Thead>
                    <Tr>
                        {COLUMNS.map(({ key, label, width, minWidth }) => (
                            <Th
                                key={key}
                                textAlign={key === 'amount' ? 'right' : 'left'}
                                cursor={
                                    key === 'viewTransaction'
                                        ? 'auto'
                                        : 'pointer'
                                }
                                width={width}
                                minWidth={minWidth}
                                onClick={() => {
                                    if (
                                        key === 'viewTransaction' ||
                                        key === 'tags'
                                    ) {
                                        return;
                                    }

                                    setSortBy({
                                        sortKey: key as SortKey,
                                        direction:
                                            sortBy.direction ===
                                            SortDirection.Asc
                                                ? SortDirection.Desc
                                                : SortDirection.Asc,
                                    });
                                }}
                                px={2}
                                whiteSpace="nowrap"
                            >
                                {label}{' '}
                                {sortBy.sortKey === key &&
                                    (sortBy.direction === SortDirection.Asc
                                        ? '↑'
                                        : '↓')}
                            </Th>
                        ))}
                    </Tr>
                </Thead>
                {loading && (
                    <LoadingTrs rowCount={5} columnCount={COLUMNS.length} />
                )}
            </Table>
            {!loading && sortedTransactionEntries.length === 0 ? (
                <FlexCol mt={12} align="center" justify="center" py={8}>
                    <Atom01 color="gray.400" />
                    <Paragraph color="gray.400" fontWeight="md" mt={4}>
                        No transactions found for the current date range.
                    </Paragraph>
                    <Paragraph color="gray.400" fontWeight="md">
                        <Link
                            textDecoration="underline"
                            onClick={open}
                            {...hoverStyleProps({ color: 'blue.400' })}
                        >
                            Add more accounts
                        </Link>{' '}
                        to see your transactions here.
                    </Paragraph>
                </FlexCol>
            ) : (
                <AutoSizer disableHeight>
                    {({ width }: { width: number }) => (
                        <List
                            height={window.innerHeight}
                            itemCount={sortedTransactionEntries.length}
                            itemSize={ROW_HEIGHT_PX}
                            width={width} // Using AutoSizer to make the table the full width of the parent container
                            itemData={{
                                transactionEntries: sortedTransactionEntries,
                                query,
                                lastViewedTransactionEntryId:
                                    lastViewedTransactionEntryId.current,
                                sortKey: sortBy.sortKey,
                                sortDirection: sortBy.direction,
                                areFiltersApplied,
                                dateRange,
                            }}
                            itemKey={
                                (index, data) =>
                                    [
                                        data.transactionEntries[index].id,
                                        data.transactionEntries[index]
                                            .categoryId,
                                        data.transactionEntries[index].date,
                                    ].join('-') // Required to rerender the table rows when sort key/direction changes, and when category/date are updated via the detail modal
                            }
                            style={{ height: '100% !important' }}
                            ref={listRef}
                            overscanCount={30}
                        >
                            {TableRow}
                        </List>
                    )}
                </AutoSizer>
            )}
        </>
    );
};

export const getDateRangeFromTransactionEntries = (
    transactionEntries: CashflowTransactionEntry[]
): DateRange => {
    if (transactionEntries.length === 0) {
        // If no transactions, default to current month
        return {
            startDate: dt().startOf('month').toDateStr(),
            endDate: dt().endOf('month').toDateStr(),
        };
    }

    const dates = transactionEntries.map((t) => t.date);
    const minDate = dates.reduce((a, b) => (a < b ? a : b));
    const maxDate = dates.reduce((a, b) => (a > b ? a : b));

    return {
        startDate: dt(minDate).toDateStr(),
        endDate: dt(maxDate).toDateStr(),
    };
};

export default CashflowTransactionEntriesTable;
